/** Why a candidate was surfaced as a possible duplicate. Ranked strongest first. */
export var DuplicatePersonMatchReason;
(function (DuplicatePersonMatchReason) {
    /** The booking's email equals the candidate's email (case-insensitive). */
    DuplicatePersonMatchReason["EXACT_EMAIL"] = "exact_email";
    /** The booking's phone equals the candidate's phone (format-insensitive). */
    DuplicatePersonMatchReason["EXACT_PHONE"] = "exact_phone";
    /** First + last name are identical after normalisation (diacritics, case, spacing). */
    DuplicatePersonMatchReason["EXACT_NAME"] = "exact_name";
    /** First + last name are a near-miss spelling of the candidate's (small edit distance). */
    DuplicatePersonMatchReason["SIMILAR_NAME"] = "similar_name";
})(DuplicatePersonMatchReason || (DuplicatePersonMatchReason = {}));
/**
 * Reason weights. Email is the strongest single signal (a person deliberately
 * gave the same mailbox); an exact name is meaningful but far weaker (namesakes
 * exist), and a near-miss name weaker still. The weights only order the list —
 * anything with at least one reason is surfaced, because the receptionist, not
 * the algorithm, makes the call.
 */
const REASON_WEIGHT = {
    [DuplicatePersonMatchReason.EXACT_EMAIL]: 100,
    [DuplicatePersonMatchReason.EXACT_PHONE]: 55,
    [DuplicatePersonMatchReason.EXACT_NAME]: 40,
    [DuplicatePersonMatchReason.SIMILAR_NAME]: 18,
};
function normalizeEmail(value) {
    return (value ?? '').trim().toLowerCase();
}
/**
 * Reduce a phone number to a comparable form: digits only, with the common
 * German international/national prefixes folded together so `+49 30 1234`,
 * `0049 30 1234` and `030 1234` compare equal. Not a validator — just enough to
 * stop trivial formatting differences from hiding a real match.
 */
function normalizePhone(value) {
    let digits = (value ?? '').replace(/\D/g, '');
    if (digits.length === 0)
        return '';
    if (digits.startsWith('00'))
        digits = digits.slice(2);
    else if (digits.startsWith('0'))
        digits = `49${digits.slice(1)}`;
    return digits;
}
/** Lowercase, strip diacritics, and collapse whitespace so `Müller` == `Muller`. */
function normalizeName(value) {
    return (value ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()
        .replace(/\s+/g, ' ');
}
function fullName(first, last) {
    return `${normalizeName(first)} ${normalizeName(last)}`.trim();
}
/** Classic Levenshtein edit distance. */
function levenshtein(a, b) {
    if (a === b)
        return 0;
    if (a.length === 0)
        return b.length;
    if (b.length === 0)
        return a.length;
    let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
    let curr = Array.from({ length: b.length + 1 }, () => 0);
    for (let i = 1; i <= a.length; i++) {
        curr[0] = i;
        for (let j = 1; j <= b.length; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
        }
        [prev, curr] = [curr, prev];
    }
    return prev[b.length];
}
/**
 * A near-miss is a small, spelling-level difference: a transposition, a dropped
 * letter, an umlaut written out. We allow an edit distance that scales with the
 * name length (≈20%, min 1) so short names aren't over-matched and long ones
 * aren't under-matched. Zero distance is an exact match, handled separately.
 */
function isNearMissName(bookingName, candidateName) {
    if (bookingName.length === 0 || candidateName.length === 0)
        return false;
    const distance = levenshtein(bookingName, candidateName);
    if (distance === 0)
        return false;
    const threshold = Math.max(1, Math.floor(Math.max(bookingName.length, candidateName.length) * 0.2));
    return distance <= threshold;
}
/**
 * Rank the candidates that could be duplicates of the booking's contact,
 * strongest first. Only candidates with at least one reason are returned; a
 * candidate with none is dropped entirely. Pure and deterministic — the same
 * inputs always yield the same order (ties break on userId so the order is
 * stable across runs).
 */
export function matchDuplicatePersons(contact, candidates) {
    const bookingEmail = normalizeEmail(contact.email);
    const bookingPhone = normalizePhone(contact.phone);
    const bookingName = fullName(contact.firstName, contact.lastName);
    const matches = [];
    for (const candidate of candidates) {
        const reasons = [];
        const candidateEmail = normalizeEmail(candidate.email);
        if (bookingEmail.length > 0 && candidateEmail === bookingEmail) {
            reasons.push(DuplicatePersonMatchReason.EXACT_EMAIL);
        }
        const candidatePhone = normalizePhone(candidate.phone);
        if (bookingPhone.length > 0 && candidatePhone === bookingPhone) {
            reasons.push(DuplicatePersonMatchReason.EXACT_PHONE);
        }
        const candidateName = fullName(candidate.firstName, candidate.lastName);
        if (bookingName.length > 0 && candidateName.length > 0) {
            if (candidateName === bookingName) {
                reasons.push(DuplicatePersonMatchReason.EXACT_NAME);
            }
            else if (isNearMissName(bookingName, candidateName)) {
                reasons.push(DuplicatePersonMatchReason.SIMILAR_NAME);
            }
        }
        if (reasons.length === 0)
            continue;
        const score = reasons.reduce((sum, reason) => sum + REASON_WEIGHT[reason], 0);
        reasons.sort((a, b) => REASON_WEIGHT[b] - REASON_WEIGHT[a]);
        matches.push({ userId: candidate.userId, score, reasons });
    }
    matches.sort((a, b) => b.score - a.score || a.userId.localeCompare(b.userId));
    return matches;
}
//# sourceMappingURL=duplicate-person-matcher.js.map